import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * Relay rule — how one venue's confirmation emails are read.
 *
 * Almost every field here is a **pattern applied to an incoming email**: the
 * facility, the title, the day, the month, the start time. When they match, an
 * email becomes a signup or a cancellation; when they stop matching — because the
 * venue changed a word in its template — bookings quietly stop being confirmed.
 *
 * That is why the fields are text areas grouped by what they extract rather than
 * a flat list of inputs, and why the type is spelled out: `IsSignup` and
 * `IsCancellation` decide what the matched mail *does*, and the difference is a
 * booking that exists or does not.
 *
 * The old page's Type is a `[Flags]`-style enum (1, 2, 4, 8, 16, 32), read as a
 * single value there and here too.
 */

const TYPES = [
  { value: 1, label: 'BadInfo', hint: 'The mail is malformed — stop, do not guess.' },
  { value: 2, label: 'Parse', hint: 'Extract the fields below from the mail.' },
  { value: 4, label: 'Irrelevant', hint: 'Ignore mails that match this.' },
  { value: 8, label: 'IsSignup', hint: 'A match confirms a booking.' },
  { value: 16, label: 'IsCancellation', hint: 'A match cancels a booking.' },
  { value: 32, label: 'Warning', hint: 'Flag it for a human to look at.' }
];

// Grouped the way the rule is read, not the way the payload lists it.
const GROUPS = [
  {
    title: 'Matching',
    hint: 'Patterns applied to the incoming email',
    fields: [
      ['primary', 'Primary'],
      ['facility', 'Facility'],
      ['title', 'Title']
    ]
  },
  {
    title: 'When',
    hint: 'What to pull out as the activity’s date and time',
    fields: [
      ['day', 'Day'],
      ['month', 'Month'],
      ['year', 'Year'],
      ['start', 'Start'],
      ['end', 'End'],
      ['duration', 'Duration']
    ]
  },
  {
    title: 'Identifiers',
    hint: 'What ties the mail back to a booking',
    fields: [
      ['externalId', 'External id'],
      ['accessCode', 'Access code']
    ]
  }
];

const RelayRuleInternal = ({ api }) => {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const [rule, setRule] = useState(null);
  const [form, setForm] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const answer = await api.relayRules.getRuleById(ruleId);
      const data = answer?.data || answer;
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setRule(data);
      setForm({ ...data });
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, ruleId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/relay-rules">← All relay rules</BackToList>
        <div className="next-note next-note--error">
          Could not load this rule.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!rule || !form) return <div className="next-page next-muted">Loading…</div>;

  const allFields = GROUPS.flatMap(group => group.fields.map(([key]) => key));
  const dirty = form.name !== rule.name
    || Number(form.type) !== Number(rule.type)
    || Number(form.order) !== Number(rule.order)
    || allFields.some(key => (form[key] || '') !== (rule[key] || ''));

  const type = TYPES.find(option => option.value === Number(form.type));
  const integrations = Array.isArray(rule.relatedIntegrations) ? rule.relatedIntegrations : [];

  const save = async () => {
    setBusy(true);
    try {
      const response = await api.relayRules.updateRule({ ...form, type: Number(form.type), order: Number(form.order) });
      if (response?.success === false) {
        setNote({ tone: 'error', text: response.message || 'The rule was not saved.' });
        return;
      }
      setNote({ tone: 'info', text: 'Rule saved. It applies to mail arriving from now on.' });
      await load();
    } catch (error) {
      setNote({ tone: 'error', text: error?.message || 'Unable to save the rule.' });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => ask({
    kind: 'danger',
    title: `Delete ${rule.name || 'this rule'}?`,
    context: `Delete rule · ${type ? type.label : ''}`.trim(),
    body: integrations.length
      ? `Mail from the ${integrations.length} integration${integrations.length === 1 ? '' : 's'} using it stops being read this way: bookings those venues confirm by email will no longer be matched.`
      : 'Mail that this rule matched stops being read this way.',
    warning: 'This cannot be undone from the admin, and nothing tells you afterwards that mail is going unmatched.',
    confirmLabel: 'Delete rule',
    onConfirm: async () => {
      const response = await api.relayRules.deleteRule(ruleId);
      if (response?.success === false) {
        setNote({ tone: 'error', text: 'Unable to delete the rule.' });
        throw new Error('delete failed');
      }
      navigate('/next/relay-rules');
    }
  });

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/relay-rules">← All relay rules</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{rule.name || 'Relay rule'}</h1>
          {type && <span className="next-pill next-pill--info">{type.label}</span>}
        </div>
        <span className="next-detail__id">{rule.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Rule</h2></div>
            <div className="next-form__grid">
              <label className="next-filter">
                <span className="next-filter__label">Name</span>
                <input
                  className="next-input"
                  value={form.name || ''}
                  onChange={event => setForm({ ...form, name: event.target.value })}
                />
              </label>

              <label className="next-filter">
                <span className="next-filter__label">Type</span>
                <select
                  className="next-input"
                  value={Number(form.type) || ''}
                  onChange={event => setForm({ ...form, type: Number(event.target.value) })}
                >
                  {TYPES.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {/* What the type actually does, because the names do not say. */}
                <span className="next-form__hint">{type ? type.hint : 'Pick what a match should do.'}</span>
              </label>

              <label className="next-filter">
                <span className="next-filter__label">Order</span>
                <input
                  className="next-input"
                  type="number"
                  value={form.order ?? ''}
                  onChange={event => setForm({ ...form, order: event.target.value })}
                />
                <span className="next-form__hint">Rules are tried in this order; the first match wins.</span>
              </label>
            </div>
          </section>

          {GROUPS.map(group => (
            <section className="next-card" key={group.title}>
              <div className="next-card__head">
                <h2>{group.title}</h2>
                <span className="next-muted">{group.hint}</span>
              </div>
              <div className="next-form__grid">
                {group.fields.map(([key, fieldLabel]) => (
                  <label className="next-filter" key={key}>
                    <span className="next-filter__label">{fieldLabel}</span>
                    <textarea
                      className="next-input next-textarea"
                      rows={2}
                      value={form[key] || ''}
                      onChange={event => setForm({ ...form, [key]: event.target.value })}
                    />
                  </label>
                ))}
              </div>
            </section>
          ))}

          <div className="next-card">
            <div className="next-form__actions">
              <button
                type="button"
                className="next-btn next-btn--primary"
                disabled={!dirty || busy}
                onClick={save}
              >
                Save changes
              </button>
              <button
                type="button"
                className="next-btn"
                disabled={!dirty || busy}
                onClick={() => setForm({ ...rule })}
              >
                Discard
              </button>
              {dirty && <span className="next-muted">Unsaved changes</span>}
            </div>
          </div>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={remove}
              >
                Delete rule
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Integrations</h2>
              <span className="next-muted">{integrations.length} using it</span>
            </div>
            {integrations.length === 0 ? (
              <p className="next-table__note next-muted">No integration uses this rule.</p>
            ) : (
              <div className="next-links">
                {integrations.map(({ integrationId }) => (
                  <span className="next-links__row" key={integrationId}>
                    <span className="next-muted">Integration</span>
                    <Guid value={integrationId} href={`/integrations?id=${integrationId}`} />
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                {
                  label: 'Test it on an email',
                  hint: 'the email page runs the relay',
                  to: '/next/emails'
                },
                { label: 'All relay rules', to: '/next/relay-rules' }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

RelayRuleInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const RelayRule = withApi(RelayRuleInternal);

RelayRule.requiresLogin = true;

export default RelayRule;
